import { UIPermitRateCard, PermitRateOutput, ReservationMonthlyAccountModificationNewTaskTypeEnum, GarageAccount, GarageAccountWithInvoicePayments, MonthlyAccount } from '@parkingbase/ecommerce-api-demo';

import { create } from 'zustand';
import { MonthlyAccountService, type MonthlyTaskSaveRequest } from '@/services/monthly-account.service';
import { PermitRateService } from '@/services/permit-rate.service';
import { UserService } from '@/services/user.service';
import { useAuthStore } from '@/stores/user.store';
import { logger } from '@/lib/logger';
import type { MonthlyRateSelection } from '@/types/monthly-rate-selection.types';

/**
 * State for the currently selected/viewed garage account in the permit detail section.
 * Set when the permit detail layout mounts; cleared when the user navigates away.
 * Extensible — add new fields here as more per-account data needs to be cached.
 */
interface SelectedAccount {
  /** The URL route param identifying this account. Used as a cache key. */
  accountId: string | null;
  /** The resolved GarageAccount object from the auth store. */
  garageAccount: GarageAccount | null;
  /** Cached invoice and payment history from the API. */
  transactions: GarageAccountWithInvoicePayments | null;
  /** True while the invoices/payments API call is in flight. */
  isLoadingTransactions: boolean;
  /** Cached sub-accounts from the API. */
  subAccounts: Record<string, MonthlyAccount> | null;
  /** True while the sub-accounts API call is in flight. */
  isLoadingSubAccounts: boolean;
}

const EMPTY_SELECTED_ACCOUNT: SelectedAccount = {
  accountId: null,
  garageAccount: null,
  transactions: null,
  isLoadingTransactions: false,
  subAccounts: null,
  isLoadingSubAccounts: false,
};
const permitRateService = new PermitRateService();

interface MonthlyAccountState {
  uiPermitRates: UIPermitRateCard[];
  isLoading: boolean;
  isSaving: boolean;

  /**
   * The currently viewed garage account and its associated cached data.
   * Populated when the permit detail layout mounts; cleared on unmount.
   */
  selectedAccount: SelectedAccount;
  /** Permit rates for the currently viewed garage (detail panel) */
  garagePermitRates: PermitRateOutput[];
  isLoadingGarageRates: boolean;
  /** True once a garage-rate fetch has completed — distinguishes "not fetched" from "fetched, empty". */
  garageRatesLoaded: boolean;
  selectedRate: MonthlyRateSelection | null;
  /**
   * PB-238: a pending rate selection stashed by the reserve → add-permit
   * hand-off. Survives `fetchGaragePermitRates` (which clears `selectedRate`),
   * so add-permit's on-mount fetch doesn't wipe the incoming selection.
   * `useAddPermit` consumes and clears it after the rates load.
   */
  pendingSelectionSeed: MonthlyRateSelection | null;

  /** Clears all cached account data (call on logout so a shared device can't leak it). */
  reset: () => void;

  fetchUIPermitRates: () => Promise<void>;
  fetchGaragePermitRates: (
    company: string,
    garageId: string,
    startDate: string,
    accountId?: string
  ) => Promise<void>;
  selectRate: (selection: MonthlyRateSelection | null) => void;
  clearGaragePermitRates: () => void;
  setPendingSelectionSeed: (selection: MonthlyRateSelection | null) => void;
  clearPendingSelectionSeed: () => void;
  createMonthlyTaskNew: (
    companyId: string,
    taskType: ReservationMonthlyAccountModificationNewTaskTypeEnum,
    monthlyAccountId: string,
    saveRequest: MonthlyTaskSaveRequest
  ) => Promise<void>;

  /**
   * Sets the currently viewed garage account and fetches its transaction data.
   * Skips the API call if data for the given accountId is already cached.
   */
  selectAccount: (garageAccount: GarageAccount, accountId: string) => Promise<void>;

  /** Fetches sub-accounts for the currently authenticated user. */
  fetchSubAccounts: () => Promise<void>;

  /** Resets selectedAccount to initial values. Called when the permit detail layout unmounts. */
  clearSelectedAccount: () => void;
}

export const useMonthlyAccountStore = create<MonthlyAccountState>((set, get) => ({
  uiPermitRates: [],
  isLoading: false,
  isSaving: false,
  selectedAccount: EMPTY_SELECTED_ACCOUNT,
  garagePermitRates: [],
  isLoadingGarageRates: false,
  garageRatesLoaded: false,
  selectedRate: null,
  pendingSelectionSeed: null,

  reset: () =>
    set({
      uiPermitRates: [],
      isLoading: false,
      isSaving: false,
      selectedAccount: { ...EMPTY_SELECTED_ACCOUNT },
      garagePermitRates: [],
      isLoadingGarageRates: false,
      garageRatesLoaded: false,
      selectedRate: null,
      pendingSelectionSeed: null,
    }),

  fetchUIPermitRates: async () => {
    // Skip if already loaded or currently loading
    if (get().uiPermitRates.length > 0 || get().isLoading) return;
    set({ isLoading: true });
    try {
      const rates = await permitRateService.getUIPermitRateCardsForEcommerce();
      set({ uiPermitRates: rates || [] });
    } catch (error) {
      logger.error('Failed to fetch UI permit rates', error, {
        feature: 'monthly-account-store',
        route: 'fetchUIPermitRates',
      });
    } finally {
      set({ isLoading: false });
    }
  },

  fetchGaragePermitRates: async (company, garageId, startDate, accountId) => {
    if (get().isLoadingGarageRates) return;
    set({
      isLoadingGarageRates: true,
      garageRatesLoaded: false,
      garagePermitRates: [],
      selectedRate: null,
    });
    try {
      // PB-3020: accountId scopes the backend's BY_ACCOUNT-tier board rates.
      const rates = await permitRateService.getPermitRatesForGarage(
        company,
        garageId,
        startDate,
        accountId
      );
      set({ garagePermitRates: rates || [] });
    } catch (error) {
      logger.error('Failed to fetch garage permit rates', error, {
        feature: 'monthly-account-store',
        route: 'fetchGaragePermitRates',
      });
    } finally {
      set({ isLoadingGarageRates: false, garageRatesLoaded: true });
    }
  },

  selectRate: (selection) => {
    set({ selectedRate: selection });
  },

  clearGaragePermitRates: () => {
    set({
      garagePermitRates: [],
      selectedRate: null,
      isLoadingGarageRates: false,
      garageRatesLoaded: false,
    });
  },

  setPendingSelectionSeed: (selection) => {
    set({ pendingSelectionSeed: selection });
  },

  clearPendingSelectionSeed: () => {
    set({ pendingSelectionSeed: null });
  },

  createMonthlyTaskNew: async (companyId, taskType, monthlyAccountId, saveRequest) => {
    const monthlyAccountService = new MonthlyAccountService();
    await monthlyAccountService.createMonthlyTaskNew(
      companyId,
      saveRequest,
      taskType,
      monthlyAccountId
    );
  },

  selectAccount: async (garageAccount, accountId) => {
    const { selectedAccount } = get();
    if (
      accountId === selectedAccount.accountId &&
      (selectedAccount.transactions !== null || selectedAccount.isLoadingTransactions)
    ) {
      return;
    }
    set({
      selectedAccount: {
        accountId,
        garageAccount,
        transactions: null,
        isLoadingTransactions: true,
        subAccounts: null,
        isLoadingSubAccounts: false,
      },
    });
    try {
      const userService = new UserService();
      const data = await userService.getTransactionsForAccount(garageAccount.documentId!);
      set({
        selectedAccount: {
          ...get().selectedAccount,
          transactions: data,
          isLoadingTransactions: false,
        },
      });
    } catch (error) {
      logger.error('Failed to fetch account transactions', error, {
        feature: 'monthly-account-store',
        route: 'selectAccount',
      });
      set({
        selectedAccount: { ...get().selectedAccount, isLoadingTransactions: false },
      });
    }
  },

  fetchSubAccounts: async () => {
    const { selectedAccount } = get();
    if (selectedAccount.subAccounts !== null || selectedAccount.isLoadingSubAccounts) {
      return;
    }
    set({
      selectedAccount: { ...get().selectedAccount, isLoadingSubAccounts: true },
    });
    try {
      // PB-3542: reuse the shared, cached /user/subaccounts map from the auth store
      // instead of calling the endpoint again — one fetch (owned by ManagePermitLayout)
      // feeds the accounts list, the permit-detail header, and this Subaccounts tab.
      const data = await useAuthStore.getState().fetchSubAccountsMap();
      set({
        selectedAccount: {
          ...get().selectedAccount,
          subAccounts: data,
          isLoadingSubAccounts: false,
        },
      });
    } catch (error) {
      logger.error('Failed to fetch sub-accounts', error, {
        feature: 'monthly-account-store',
        route: 'fetchSubAccounts',
      });
      set({
        selectedAccount: { ...get().selectedAccount, isLoadingSubAccounts: false },
      });
    }
  },

  clearSelectedAccount: () => {
    set({ selectedAccount: EMPTY_SELECTED_ACCOUNT });
  },
}));
